/*
  ==============================================================================
  Accordion — View sem hidratação (HTML nativo <details>/<summary>)
  ==============================================================================
  Substitui o View do addon @eeacms/volto-accordion-block (que abre/fecha via
  React.useState + react-animate-height e, portanto, só funciona DEPOIS da
  hidratação). Aqui o abre/fecha é 100% nativo do navegador: funciona com zero
  JavaScript, é acessível por padrão (teclado + leitor de tela) e o estado
  inicial vem correto do SSR (atributo `open` calculado no render).

  Observação de arquitetura: este bloco NÃO segue o padrão de classe vanilla
  (ex.: theme/js/BannerTopoCarousel) de propósito — aquele padrão é para
  widgets que dependem de JS (Glide); aqui o objetivo é justamente rodar SEM JS.

  O editor continua usando o `edit` React do addon (ver config/blocks/enhancers/accordion.ts); só a
  página publicada usa esta versão nativa. As cores/espaçamento vêm das
  variáveis CSS expostas pelo wrapper `.mpf-accordion-color` (ver _accordion.scss).
  ==============================================================================
*/

import React from 'react';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';
import withBlockExtensions from '@plone/volto/helpers/Extensions/withBlockExtensions';
import {
  getBlocksFieldname,
  getBlocksLayoutFieldname,
  blockHasValue,
} from '@plone/volto/helpers/Blocks/Blocks';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import rightSVG from '@plone/volto/icons/right-key.svg';

import type {
  AccordionBlockData,
  AccordionPanel,
  AccordionPanelEntry,
  AccordionTitleSize,
  AccordionViewProps,
} from './types';

const DEFAULT_TITLE_SIZE: AccordionTitleSize = 'h3';

/**
 * Extrai os painéis na ordem definida por blocks_layout.
 * Reimplementado localmente (em vez de importar do .jsx do addon) para manter
 * o View tipado e desacoplado de mudanças internas do addon.
 */
const getPanels = (data?: AccordionBlockData['data']): AccordionPanelEntry[] =>
  (data?.blocks_layout?.items || []).map((id) => [id, data?.blocks?.[id]]);

/** Um painel só é renderizado se tiver título ou algum bloco com conteúdo. */
const accordionPanelHasValue = (panel?: AccordionPanel): boolean => {
  if (!panel) return false;
  if (panel.title && panel.title.length > 0) return true;

  const blocksFieldname = getBlocksFieldname(panel);
  const blocksLayoutFieldname = getBlocksLayoutFieldname(panel);
  const items = panel[blocksLayoutFieldname]?.items || [];
  return items.some((blockId) =>
    blockHasValue(panel[blocksFieldname]?.[blockId]),
  );
};

const AccordionView = (props: AccordionViewProps) => {
  const { data, id: blockId, className } = props;
  const location = useLocation();
  const metadata = props.metadata || props.properties;

  const panels = getPanels(data.data).filter(([, panel]) =>
    accordionPanelHasValue(panel),
  );

  // URL terminando em /diff abre todos os painéis (paridade com o addon).
  const diffView = location?.pathname?.endsWith('/diff') ?? false;

  // Modo exclusivo (apenas um aberto): atributo `name` nativo agrupa os
  // <details>. Só agrupa quando há um id de bloco para garantir unicidade.
  const exclusiveGroup =
    data.non_exclusive === false && blockId ? `mpf-acc-${blockId}` : undefined;

  const iconOnRight = data.right_arrows !== false;
  const TitleTag = (data.title_size ||
    DEFAULT_TITLE_SIZE) as AccordionTitleSize;

  return (
    // Classes com namespace próprio (mpf-accordion__*) de propósito: o addon
    // carrega um editor.less GLOBAL que estiliza `.accordion-title` /
    // `.align-arrow-*` para a estrutura DELE (ícone antes do texto). Reusar
    // esses nomes invertia a seta e mascarava o title_size. Aqui o CSS é só
    // nosso (ver _accordion.scss). As cores vêm das vars do .mpf-accordion-color.
    <div className={cx('mpf-accordion', className)}>
      {data.headline && (
        <h2 className="mpf-accordion__headline">{data.headline}</h2>
      )}

      {panels.map(([panelId, panel], index) => {
        // Fechado por padrão (collapsed=true). Quando collapsed=false, o
        // primeiro painel inicia aberto — igual ao comportamento do addon.
        const open = diffView || (data.collapsed === false && index === 0);

        return (
          <details
            key={panelId}
            id={panelId}
            className="mpf-accordion__item"
            // `name` agrupa os painéis para exclusividade nativa do browser.
            // Em navegadores antigos é ignorado (degrada para múltiplos abertos).
            {...(exclusiveGroup ? { name: exclusiveGroup } : {})}
            {...(open ? { open: true } : {})}
          >
            <summary
              className={cx('mpf-accordion__header', {
                'mpf-accordion__header--arrow-left': !iconOnRight,
              })}
            >
              <TitleTag className="mpf-accordion__title">
                <span>{panel?.title}</span>
              </TitleTag>
              <Icon
                name={rightSVG}
                className="mpf-accordion__arrow"
                size="24px"
              />
            </summary>

            <div className="mpf-accordion__panel">
              <RenderBlocks
                {...props}
                location={location}
                metadata={metadata}
                content={panel}
              />
            </div>
          </details>
        );
      })}
    </div>
  );
};

AccordionView.displayName = 'AccordionNoHydrationView';

export default withBlockExtensions(AccordionView);
